import { useQuery } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { LandscapeRing, ServiceHealth, TenantRingEntry } from '../api'
import { PageDescription } from '../components/PageDescription'

const STATUS_STYLES: Record<string, { dot: string; badge: string; label: string }> = {
    healthy:  { dot: 'bg-green-400',  badge: 'bg-green-500/15 text-green-400 ring-1 ring-green-500/30',  label: 'healthy' },
    degraded: { dot: 'bg-amber-400',  badge: 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30',  label: 'degraded' },
    unhealthy:{ dot: 'bg-red-400',    badge: 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30',         label: 'unhealthy' },
    unknown:  { dot: 'bg-slate-500',  badge: 'bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/30',   label: 'unknown' },
}

const TENANT_STATUS_DOT: Record<string, string> = {
    Active:           'bg-green-400',
    Provisioned:      'bg-sky-400',
    Suspended:        'bg-amber-400',
    Deprovisioned:    'bg-slate-500',
    PendingBootstrap: 'bg-amber-400',
}

const TENANT_STATUS_BADGE: Record<string, string | undefined> = {
    PendingBootstrap: 'Pending bootstrap',
    Suspended:        'Suspended',
    Deprovisioned:    'Deprovisioned',
}

function fmt(n: number | null | undefined, decimals = 1): string {
    return n == null ? '—' : n.toFixed(decimals)
}

function StatusBadge({ status }: { status: string }) {
    const s = STATUS_STYLES[status] ?? STATUS_STYLES['unknown']!
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${s.badge}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
            {s.label}
        </span>
    )
}

function ServiceRow({ svc }: { svc: ServiceHealth }) {
    const s = STATUS_STYLES[svc.status] ?? STATUS_STYLES['unknown']!
    return (
        <div className="flex items-center gap-2 py-2 border-b border-slate-700/40 last:border-0">
            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${s.dot}`} />
            <span className="text-xs font-medium text-slate-300 w-24 shrink-0 font-mono truncate">{svc.name}</span>
            <div className="flex items-center gap-3 text-[11px] text-slate-400 ml-auto tabular-nums">
                <span title="Requests per minute">
                    <span className="text-slate-500 mr-0.5">rpm</span>
                    <span className="text-slate-300">{fmt(svc.requestsPerMin, 0)}</span>
                </span>
                <span title="Error rate">
                    <span className="text-slate-500 mr-0.5">err</span>
                    <span className={svc.errorPct != null && svc.errorPct > 1 ? 'text-amber-400' : 'text-slate-300'}>
                        {fmt(svc.errorPct, 2)}%
                    </span>
                </span>
                <span title="p95 response time">
                    <span className="text-slate-500 mr-0.5">p95</span>
                    <span className={svc.p95Ms != null && svc.p95Ms > 500 ? 'text-amber-400' : 'text-slate-300'}>
                        {fmt(svc.p95Ms, 0)}ms
                    </span>
                </span>
            </div>
        </div>
    )
}

function TenantRow({ tenant }: { tenant: TenantRingEntry }) {
    const dot = TENANT_STATUS_DOT[tenant.status] ?? 'bg-slate-500'
    const badgeLabel = TENANT_STATUS_BADGE[tenant.status]
    const badgeStyle = tenant.status === 'PendingBootstrap'
        ? 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30'
        : 'bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/30'
    return (
        <div className="flex items-center gap-2 py-1.5 border-b border-slate-700/30 last:border-0">
            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${dot}`} />
            <span className="text-xs text-slate-300 font-mono truncate flex-1">{tenant.slug}</span>
            {badgeLabel ? (
                <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold shrink-0 ${badgeStyle}`}>
                    {badgeLabel}
                </span>
            ) : (
                <span className="text-[10px] text-slate-500 shrink-0 truncate max-w-[80px]">{tenant.displayName}</span>
            )}
        </div>
    )
}

function RingCard({ ring }: { ring: LandscapeRing }) {
    return (
        <div className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50 bg-slate-800/60">
                <span className="text-base font-semibold text-white">{ring.displayName}</span>
                <StatusBadge status={ring.healthStatus} />
            </div>

            {/* Services section */}
            <div className="px-4 pt-2 pb-1">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Services</span>
                {ring.services.length === 0 ? (
                    <p className="text-xs text-slate-500 py-2">No service data</p>
                ) : (
                    ring.services.map(svc => <ServiceRow key={svc.name} svc={svc} />)
                )}
            </div>

            {/* Tenants section */}
            <div className="px-4 pt-2 pb-2 flex-1 border-t border-slate-700/30 mt-1">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Tenants{ring.tenants.length > 0 && <span className="ml-1 text-slate-600">({ring.tenants.length})</span>}
                </span>
                {ring.tenants.length === 0 ? (
                    <p className="text-xs text-slate-500 py-2">No tenants assigned</p>
                ) : (
                    <div className="mt-1 max-h-48 overflow-y-auto">
                        {ring.tenants.map(t => <TenantRow key={t.tenantId} tenant={t} />)}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="px-4 py-1.5 border-t border-slate-700/30 flex items-center justify-between">
                <span className="text-[10px] text-slate-600">
                    {ring.isPerRingData ? 'per-ring metrics' : 'global aggregate'}
                </span>
                <span className="text-[10px] text-slate-600">
                    as of {new Date(ring.healthAsOf).toLocaleTimeString()}
                </span>
            </div>
        </div>
    )
}

export function LandscapePage() {
    const { data, isLoading, isError, error, dataUpdatedAt, refetch, isFetching } = useQuery({
        queryKey: ['landscape'],
        queryFn: releaseApi.getLandscape,
        refetchInterval: 60_000,
    })

    return (
        <>
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <div>
                        <span className="text-sm font-semibold text-slate-200">Landscape</span>
                        <PageDescription subtitle="Ring health and tenant assignments" storageKey="desc-landscape" detail="Shows which tenants are assigned to each ring and the sync/health status of each ring's services. Use this to see which ring a tenant is on and whether their services are running the latest release." />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                        {dataUpdatedAt > 0 && (
                            <span>Updated {new Date(dataUpdatedAt).toLocaleTimeString()}</span>
                        )}
                        <button
                            onClick={() => void refetch()}
                            disabled={isFetching}
                            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                            {isFetching ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 w-full">
                {isLoading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-64 rounded-xl border border-slate-700 bg-slate-800/40 animate-pulse" />
                        ))}
                    </div>
                )}

                {isError && (
                    <div className="rounded-xl border border-red-800 bg-red-950/50 p-6 text-red-300 text-sm">
                        Failed to load landscape data:{' '}
                        {error instanceof Error ? error.message : 'Unknown error'}
                    </div>
                )}

                {data && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.rings.map(ring => (
                            <RingCard key={ring.ring} ring={ring} />
                        ))}
                    </div>
                )}
            </main>
        </>
    )
}
